import {
	FrontendSampleInstance,
	FrontendOrder,
	FrontendCompanyTestCategory,
	FrontendCompanySampleMatrix,
	FrontendCompanyTestHierarchyNode,
	FrontendCompanyTestResultField,
	FrontendCompanyTestExpectationField,
} from "node-library/dist/portal";
import { TFunction } from "i18next";
import { formatCustomDate } from "node-library/dist/helpers/date_helper";
import { FrontendExpectationInputState } from "node-library/dist/frontendInputStates";

export function formatInstanceExcelData(
	order: FrontendOrder,
	testResultAndExpectationFields: Array<FrontendCompanyTestResultField>,
	testCategory: FrontendCompanyTestCategory,
	sampleInstances: Array<FrontendSampleInstance>,
	translation: TFunction<"translation", undefined>,
): string[][] {
	const sortedInstances = [...sampleInstances].sort((a, b) => {
		const idA = a.sample.fullSampleId || "";
		const idB = b.sample.fullSampleId || "";
		return idB.localeCompare(idA); // descending order
	});

	const expectationFieldIdToNameMap = new Map<string, string>();
	for (const field of testResultAndExpectationFields) {
		const expectationField: FrontendCompanyTestExpectationField | undefined =
			field.expectationField;
		if (!expectationField) {
			continue;
		}
		const fieldId: string = expectationField.expectationFieldId;
		if (field) {
			expectationFieldIdToNameMap.set(fieldId, field.fieldName);
		}
	}

	const excelData: Array<Array<string>> = sortedInstances.map((instance) => {
		const expectation: FrontendExpectationInputState | undefined =
			instance.sampleTestInfo?.expectationInputs;

		let expectationString: string = "";
		if (expectation) {
			expectationString = formatExpectationToString(
				expectation,
				testResultAndExpectationFields,
				expectationFieldIdToNameMap,
			);
		}
		let result: Array<string> = [];
		switch (testCategory.testCategoryName) {
			case "Cannabis":
				//Expectation + description, report id, matrix, sample id, receive date, due date, weight
				result = [
					[
						expectationString.trim(),
						(instance.otherInstructions || "").replace(/[\r\n]+/g, " ").trim(),
					]
						.filter((s) => s) // remove empty strings
						.join("; "),
					"",
					instance.sample.isCustomMatrix
						? instance.sample.matrix
						: (
								testCategory.sampleMatrices.find((matrix) => {
									return matrix.matrixId == instance.sample.matrixId;
								}) as FrontendCompanySampleMatrix
							).subMatrix,
					`${instance.sample.fullSampleId || ""}${
						instance.isRetest ? ` ${translation("Retest")}` : ""
					}${instance.isAdditionalTest ? ` ${translation("Add")}` : ""} `,
					formatCustomDate(instance.receivedTime).split(" | ")[0],
					formatCustomDate(instance.expectedCompletionTime as Date).split(
						" | ",
					)[0],
					`${instance.weight} ${instance.weightUnit}`,
				];
				//tests
				result.push(
					...getInstanceTestsExcelExportString(
						instance,
						testCategory.hierarchyNodes,
						translation,
					),
				);
				break;
			case "Environmental (Water)":
			case "Environmental (Soil)":
				//Expectation + description, report id, sample id, receive date, due date, matrix(W/S)
				const matrix: string =
					testCategory.testCategoryName == "Environmental (Water)" ? "W" : "S";
				result = [
					[
						expectationString.trim(),
						(instance.otherInstructions || "").replace(/[\r\n]+/g, " ").trim(),
					]
						.filter((s) => s) // remove empty strings
						.join("; "),
					"",

					`${instance.sample.fullSampleId || ""}${
						instance.isRetest ? ` ${translation("Retest")}` : ""
					}${instance.isAdditionalTest ? ` ${translation("Add")}` : ""} `,
					formatCustomDate(instance.receivedTime).split(" | ")[0],
					formatCustomDate(instance.expectedCompletionTime as Date).split(
						" | ",
					)[0],
					`${matrix}`,
				];
				//tests
				result.push(
					...getEnvironmentalInstanceTestsExcelExportString(
						instance,
						testCategory.hierarchyNodes,
						translation,
					),
				);
				break;
			case "Natural Health Product":
			case "Other":
				//Expectation + description, report id, sample id, receive date, due date
				result = [
					[
						expectationString.trim(),
						(instance.otherInstructions || "").replace(/[\r\n]+/g, " ").trim(),
					]
						.filter((s) => s) // remove empty strings
						.join("; "),
					"",

					`${instance.sample.fullSampleId || ""}${
						instance.isRetest ? ` ${translation("Retest")}` : ""
					}${instance.isAdditionalTest ? ` ${translation("Add")}` : ""} `,
					formatCustomDate(instance.receivedTime).split(" | ")[0],
					formatCustomDate(instance.expectedCompletionTime as Date).split(
						" | ",
					)[0],
				];
				//tests
				result.push(
					...getOtherInstanceTestsExcelExportString(
						instance,
						testCategory.hierarchyNodes,
						translation,
					),
				);
				break;
		}

		//client info
		//org name, sample code, sample description, comments,
		result.push(
			...[
				order.clientCompanyName,
				instance.sample.lotCode,
				(instance.sample.sampleDescription || "").replace(/[\r\n]+/g, " "),
				"",
			],
		);

		return result;
	});
	return excelData;
}

export function formatDataForGoogleSheets(data: string[][]): string {
	return data
		.map((row) => row.join("\t")) // Join each row with tabs between columns
		.join("\n"); // Join rows with a newline
}

export function getInstanceTestsExcelExportString(
	sampleInstance: FrontendSampleInstance,
	hierarchyNodes: Array<FrontendCompanyTestHierarchyNode>,
	translation: TFunction<"translation", undefined>,
): Array<string> {
	const selectedTestsNodeIds: Array<string> = (
		sampleInstance.sampleTestInfo?.selectedTests || []
	).map((test) => {
		return test.selectedNodeId;
	});

	const expectedNodeKeysOrder: Array<string> = [
		"cannabinoid",
		"moistureContent",
		"terpene",
		"pesticides",
		"mycotoxins",
		"heavyMetals",
		"TAMC",
		"TYMC",
		"BTGN",
		"Salmonella",
		"EColi",
		"Staphylococcus",
		"Pseudomonas",
		"Micro5", // placeholder for micro-5
		"Micro7", // placeholder for micro-7
		"foreignMatter",
		"residualSolvent",
		"viroids",
	];

	const nodeKeyToNodeId = new Map<string, string>();
	for (const node of hierarchyNodes) {
		nodeKeyToNodeId.set(node.nodeKey, node.nodeId);
	}

	// Map the expected keys to "X" or "" depending on selection
	const allTestsString: Array<string> = expectedNodeKeysOrder.map((key) => {
		const nodeId = nodeKeyToNodeId.get(key);
		return nodeId && selectedTestsNodeIds.includes(nodeId) ? "X" : "";
	});

	//other tests
	const otherTests: Array<string> = [];

	// Add free-text test description, if any
	const additional = sampleInstance.sampleTestInfo?.additionalTestDescription;
	if (additional) {
		otherTests.push(additional);
	}
	// Map for optional extra nodes that should appear in "other tests" section
	const otherTestKeys = [
		{ key: "waterActivity", label: translation("WaterActivity") },
		{ key: "sexGenderTesting", label: translation("SexGenderTesting") },
		{ key: "fusarium", label: translation("Fusarium") },
		{ key: "pythium", label: translation("Pythium") },
		{ key: "tobaccoMosaicVirus", label: translation("TobaccoMosaicVirus") },
		{ key: "powderyMildew", label: translation("PowderyMildew") },
	];

	for (const { key, label } of otherTestKeys) {
		const nodeId = nodeKeyToNodeId.get(key);
		if (nodeId && selectedTestsNodeIds.includes(nodeId)) {
			otherTests.push(label);
		}
	}

	const otherTestsString: string = otherTests.join(", ");
	allTestsString.push(otherTestsString);

	return allTestsString;
}

export function getEnvironmentalInstanceTestsExcelExportString(
	sampleInstance: FrontendSampleInstance,
	hierarchyNodes: Array<FrontendCompanyTestHierarchyNode>,
	translation: TFunction<"translation", undefined>,
): Array<string> {
	const selectedTestsNodeIds: Array<string> = (
		sampleInstance.sampleTestInfo?.selectedTests || []
	).map((test) => {
		return test.selectedNodeId;
	});

	const expectedNodeKeysOrder: Array<string> = [
		"Metal",
		"pH",
		"PAHs",
		"PCBs",
		"Conductivity",
		"SAR",
		"Sodium",
		"VOC",
		"BTEX",
		"PHC",
		"PHC-F1",
		"PHC-F2-4",
		"PHC-F4G",
		"Cyanide",
		"Boron",
		"ChromiumVI",
		"Moisture",
		"Mercury",
		"FullMetal",
		"Ecoli", //placeholders
		"TotalColiform", //placeholders

		"FecalColiform", //placeholders

		"Pseudomonas", //placeholders

		"Staphylococcus", //placeholders

		"Heterotrophic", //placeholders

		"Alkanity", //placeholders

		"FreeChlorine", //placeholders

		"TotalChlorine", //placeholders
		"TSS", //placeholders
	];

	const nodeKeyToNodeId = new Map<string, string>();
	for (const node of hierarchyNodes) {
		nodeKeyToNodeId.set(node.nodeKey, node.nodeId);
	}

	// Map the expected keys to "X" or "" depending on selection
	const allTestsString: Array<string> = expectedNodeKeysOrder.map((key) => {
		const nodeId = nodeKeyToNodeId.get(key);
		return nodeId && selectedTestsNodeIds.includes(nodeId) ? "X" : "";
	});

	//other tests
	const otherTests: Array<string> = [];

	// Add free-text test description, if any
	const additional = sampleInstance.sampleTestInfo?.additionalTestDescription;
	if (additional) {
		otherTests.push(additional);
	}

	const otherTestsString: string = otherTests.join(", ");
	allTestsString.push(otherTestsString);

	return allTestsString;
}

export function getOtherInstanceTestsExcelExportString(
	sampleInstance: FrontendSampleInstance,
	hierarchyNodes: Array<FrontendCompanyTestHierarchyNode>,
	translation: TFunction<"translation", undefined>,
): Array<string> {
	const selectedTestsNodeIds: Array<string> = (
		sampleInstance.sampleTestInfo?.selectedTests || []
	).map((test) => {
		return test.selectedNodeId;
	});

	const expectedNodeKeysOrder: Array<string> = [
		"heavyMetals",
		"moistureContent",
		"PAHs",
		"Conductivity",
		"BTEX",
		"organoleptic",
		"disintegration",
		"weightVariation",
		"TAC",

		"Metal",
		"Micro-5", //placeholder
		"YM",

		"Ecoli",

		"Salmonella",
		"Staphylococcus",
		"Pseudomonas",

		"Total Aerobic Count", //placeholders
		"Pesticides",
		"Phosphate",
	];

	const nodeKeyToNodeId = new Map<string, string>();
	for (const node of hierarchyNodes) {
		nodeKeyToNodeId.set(node.nodeKey, node.nodeId);
	}

	// Map the expected keys to "X" or "" depending on selection
	const allTestsString: Array<string> = expectedNodeKeysOrder.map((key) => {
		const nodeId = nodeKeyToNodeId.get(key);
		return nodeId && selectedTestsNodeIds.includes(nodeId) ? "X" : "";
	});

	//other tests
	const otherTests: Array<string> = [];

	// Add free-text test description, if any
	const additional = sampleInstance.sampleTestInfo?.additionalTestDescription;
	if (additional) {
		otherTests.push(additional);
	}

	// Map for optional extra nodes that should appear in "other tests" section
	const otherTestKeys = [
		{ key: "waterDetermination", label: translation("WaterDetermination") },
	];

	for (const { key, label } of otherTestKeys) {
		const nodeId = nodeKeyToNodeId.get(key);
		if (nodeId && selectedTestsNodeIds.includes(nodeId)) {
			otherTests.push(label);
		}
	}

	const otherTestsString: string = otherTests.join(", ");
	allTestsString.push(otherTestsString);

	return allTestsString;
}

function formatExpectationToString(
	expectationInputs: FrontendExpectationInputState,
	fields: FrontendCompanyTestResultField[],
	expectationFieldIdToNameMap: Map<string, string>,
): string {
	const formattedEntries: string[] = [];

	for (const field of fields) {
		const expectationField = field.expectationField;
		if (!expectationField) continue;

		const input = expectationInputs[expectationField.expectationFieldId];
		if (!input) continue;

		let value = "";
		if (input.type === "value") {
			if (input.value.trim() === "") continue;
			value = input.value;
		} else if (input.type === "range") {
			const min = input.min?.trim();
			const max = input.max?.trim();
			const unitId = input.unitId;

			const unit =
				field.availableUnits.find((unit) => {
					return unit.unitId == unitId;
				})?.unit || "";

			if (min && max) {
				value = `${min}${unit} ~ ${max}${unit}`;
			} else if (min) {
				value = `≥ ${min}${unit}`;
			} else if (max) {
				value = `≤ ${max}${unit}`;
			} else {
				continue;
			}
		}

		const fieldName =
			expectationFieldIdToNameMap.get(expectationField.expectationFieldId) ??
			field.fieldName;

		formattedEntries.push(`${fieldName}: ${value}`);
	}

	return formattedEntries.join("; ");
}
